import React, { useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Grid, Stack } from '@mui/material';
import useCoupons from 'api/useCoupons';
import useOffers from 'api/useOffers';
import {
  Coupon,
  EPublisherSolutionModel,
  Offer,
  PromoCode
} from 'common/contracts';
import { EButtonColor, ELocalStorageKeys, OfferType } from 'constants/enums';
import dayjs from 'dayjs';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import { localStorageUtil } from 'utils/localStorageUtil';
import * as yup from 'yup';

import AcContentWrapper from '../../../components/AcContentWrapper/AcContentWrapper';
import AcViewWrapper from '../../../components/AcViewWrapper/AcViewWrapper';
import DialogModal from '../../../components/Dialog/Dialog';
import PageTopBar from '../../../components/Topbar/PageTopBar';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ICouponsFormProps, UpsertCouponWithPromoCodeDto } from '../types';
import {
  buildCouponValidationSchema,
  checkUniqueness,
  deriveExpirationMode,
  generateCouponDto,
  generatePromoCodesDto,
  getOffersForSelect} from '../utils';

import CouponsGeneralSettings from './components/CouponsGeneralSettings';
import CouponsOffersSettings from './components/CouponsOffersSettings';
import CouponsPlayerSettings from './components/CouponsPlayersSettings/CouponsPlayerSettings';
import CouponsPromoCodes from './components/CouponsPromoCodes';

import '../../../style/forms.scss';

export const CouponsForm: React.FC<ICouponsFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { couponId } = useParams();

  const { getSingleCoupon, upsertCoupon, isPromoCodeActive } = useCoupons(
    publisherId,
    couponId
  );
  const { getCoupons } = useCoupons(publisherId);

  const initialStartsAt = (getSingleCoupon.data as any)?.startsAt || null;
  const initialExpiredBy = getSingleCoupon.data?.expiredBy || null;
  const initialExpirationMode = deriveExpirationMode(
    initialStartsAt,
    initialExpiredBy
  );

  const [neverExpires, setNeverExpires] = useState<boolean>(
    initialExpirationMode === 'no-expiration'
  );

  useEffect(() => {
    const mode = deriveExpirationMode(
      getSingleCoupon.data?.startsAt,
      getSingleCoupon.data?.expiredBy
    );
    setNeverExpires(mode === 'no-expiration');
  }, [getSingleCoupon.data?.expiredBy, getSingleCoupon.data?.startsAt]);

  const [isUnSavedChangesModalOpen, setIsUnSavedChangesModalOpen] =
    useState(false);

  const [promoCodes, setPromoCodes] = useState<
    {
      _id: string;
      name: string;
      maxRedemptions: number;
      creationDate: string;
      active: boolean;
    }[]
  >([]);
  const [promoCode, setPromoCode] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState<string | number>('');

  useEffect(() => {
    setPromoCodes(
      (getSingleCoupon.data?.promoCodes as any)?.map((promoCode: any) => ({
        _id: promoCode.id || '',
        name: promoCode.name || '',
        maxRedemptions: promoCode.maxRedemptions || 0,
        active: promoCode.active || false
      }))
    );
  }, [getSingleCoupon.data]);

  const { fetchAllOffers } = useOffers({});

  const [offersMap, setOffersMap] = useState<{
    bundles: Offer[];
    promotions: Offer[];
    popups: Offer[];
  }>({
    bundles: [],
    promotions: [],
    popups: []
  });

  const [isLoadingOffers, setIsLoadingOffers] = useState(true);

  useEffect(() => {
    const fetchAllOffersData = async () => {
      try {
        setIsLoadingOffers(true);

        const [bundleOffers, specialOffers, popupOffers] = await Promise.all([
          fetchAllOffers({
            offerType: OfferType.BUNDLE,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.SPECIAL_OFFER,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.POPUP,
            fetchOnlyActiveOffers: true
          })
        ]);

        setOffersMap({
          bundles: bundleOffers,
          promotions: specialOffers,
          popups: popupOffers
        });
      } catch (error) {
        console.error('Error fetching all offers:', error);
        showToast({
          message: 'Failed to load offers',
          status: EToasterStatus.ERROR
        });
      } finally {
        setIsLoadingOffers(false);
      }
    };

    if (publisherId) {
      fetchAllOffersData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisherId]);

  const isCheckoutUser =
    localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
    EPublisherSolutionModel.CHECKOUT;

  const supportedOffersBySku = isCheckoutUser
    ? []
    : getSingleCoupon.data?.supportedOffersBySku ??
      (isLoadingOffers
        ? []
        : getOffersForSelect(
            offersMap,
            getSingleCoupon.data?.supportedOffersByType || []
          )?.map((offer) => offer.key));

  let couponSchema = buildCouponValidationSchema({
    checkUniqueness,
    getCouponsData: getCoupons.data,
    currentCouponName: getSingleCoupon.data?.name || '',
    isEdit: Boolean(edit),
    isCheckoutUser
  });

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    errors,
    dirty,
    isSubmitting,
    touched,
    isValid,
    setFieldValue,
    setFieldTouched
  } = useFormik({
    validateOnMount: true,
    validationSchema: couponSchema,
    enableReinitialize: true,
    initialValues: {
      name: getSingleCoupon.data?.name || '',
      active: getSingleCoupon.data?.active ?? true,
      discountPercentage: getSingleCoupon.data?.discountPercentage || '',
      maxRedemptionsPerCustomer:
        getSingleCoupon.data?.maxRedemptionsPerCustomer || '',
      expiredBy: initialExpiredBy,
      startsAt: initialStartsAt as string | null,
      expirationMode: initialExpirationMode as
        | 'no-expiration'
        | 'start-only'
        | 'start-and-end',
      firstTimePurchase: getSingleCoupon.data?.firstTimePurchase || false,
      supportedOffersBySku,
      promoCodes: getSingleCoupon.data?.promoCodes || [],
      allowedPlayers: getSingleCoupon.data?.allowedPlayers || []
    },
    onSubmit: async (values, helpers) => {
      const promoCodesData = generatePromoCodesDto(values.promoCodes, couponId);
      const couponData = generateCouponDto(values, neverExpires);

      const upsertCouponData: UpsertCouponWithPromoCodeDto = {
        coupon: couponData,
        promoCodes: promoCodesData
      };

      const isEdit = Boolean(edit);

      const onSuccess = async () => {
        if (edit) {
          await getSingleCoupon.refetch();
        }
        if (getSingleCoupon.data) {
          helpers.setValues({
            ...values,
            ...getSingleCoupon.data,
            promoCodes: (getSingleCoupon.data.promoCodes || []).map(
              (promoCode) => ({
                ...promoCode,
                fresh: false
              })
            ),
            startsAt: (getSingleCoupon.data?.startsAt ?? null) as string | null,
            expirationMode: (getSingleCoupon.data?.expiredBy
              ? 'start-and-end'
              : getSingleCoupon.data?.startsAt
                ? 'start-only'
                : 'no-expiration') as unknown as
              | 'no-expiration'
              | 'start-only'
              | 'start-and-end'
          });
        }
        showToast({
          message: `Coupon ${isEdit ? 'updated' : 'created'} successfully`,
          status: EToasterStatus.SUCCESS
        });

        if (!isEdit) {
          navigate(-1);
        }
      };

      const onError = (error: any) => {
        const serverError = error?.response?.data?.message;
        showToast({
          message:
            serverError || `Error ${isEdit ? 'updating' : 'creating'} coupon`,
          status: EToasterStatus.ERROR
        });
      };

      upsertCoupon.mutate(upsertCouponData, {
        onSuccess,
        onError
      });
    }
  });

  const isSaveEnabled = dirty && isValid && !upsertCoupon.isLoading;

  const getOffersForSelectBound = (supportedOffersByType?: string[]) =>
    getOffersForSelect(offersMap, supportedOffersByType);

  return (
    <>
      <AcViewWrapper
        header={
          <PageTopBar
            disable={false}
            headline={`${edit ? 'Edit' : 'New'} Coupon ${
              edit
                ? `(${
                    !getSingleCoupon.isLoading && getSingleCoupon.data
                      ? getSingleCoupon.data.name
                      : '...'
                  })`
                : ''
            }`}
            buttons={[
              {
                text: 'Save',
                action: () => submitForm(),
                disabled: !isSaveEnabled
              }
            ]}
            backFunction={() => {
              if (isSaveEnabled) {
                setIsUnSavedChangesModalOpen(true);
              } else {
                navigate(-1);
              }
            }}
          />
        }
      >
        <AcContentWrapper className="formContent">
          <Grid container pt={'24px'}>
            <Grid item xs={12}>
              <Stack>
                <CouponsGeneralSettings
                  couponFormValues={values}
                  edit={edit}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors as FormikErrors<Coupon>}
                  touched={touched as FormikTouched<Coupon>}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  neverExpires={neverExpires}
                  dup={dup}
                  updatedAt={getSingleCoupon.data?.updatedAt}
                  setNeverExpires={setNeverExpires}
                />

                {!isCheckoutUser && (
                  <CouponsOffersSettings
                    supportedOffersBySku={values.supportedOffersBySku}
                    getOffersForSelect={getOffersForSelectBound}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors as FormikErrors<Coupon>}
                    touched={touched as FormikTouched<Coupon>}
                    setFieldValue={setFieldValue}
                  />
                )}

                <CouponsPlayerSettings
                  couponFormValues={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  errors={errors as FormikErrors<Coupon>}
                  touched={touched as FormikTouched<Coupon>}
                  setFieldValue={setFieldValue}
                  dup={dup}
                />
                <CouponsPromoCodes
                  couponFormValues={values}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  maxRedemptions={maxRedemptions}
                  setMaxRedemptions={setMaxRedemptions}
                  isPromoCodeActive={isPromoCodeActive}
                />
              </Stack>
            </Grid>
          </Grid>
        </AcContentWrapper>
      </AcViewWrapper>

      <DialogModal
        isOpen={isUnSavedChangesModalOpen}
        headline="Unsaved Changes"
        text="You changed a promo code but didn't save the coupon. Leaving will lose your changes."
        buttons={[
          {
            text: 'Proceed',
            color: EButtonColor.ERROR,
            variant: 'outlined',
            func: () => {
              navigate(-1);
            }
          },
          {
            text: 'Save Coupon',
            color: EButtonColor.SECONDARY,
            variant: 'contained',
            func: () => {
              submitForm();
              setIsUnSavedChangesModalOpen(false);
            }
          }
        ]}
        closeDialog={() => setIsUnSavedChangesModalOpen(false)}
      />
    </>
  );
};
